/**
 * Handle form changes and update the current state.
 *
 * @param {string} key - The key of the form field.
 * @param {string | number | any} value - The new value of the form field.
 * @param {Function} setFunction - The function to update the form state.
 */
export const handleChange = (
  key: string,
  value: string | number | any,
  setFunction: Function,
) => {
  setFunction((prev: any) => {
    return {
      ...prev,
      [key]: value,
    };
  });
};

/**
 * Check if the form is valid.
 *
 * @param {object} form - The form object.
 * @param {string[]} mandatoryFields - The array of mandatory field keys.
 * @returns {boolean} - Returns true if the form is valid, false otherwise.
 */
export const checkIfFormValid = (
  form: any,
  mandatoryFields: string[],
): boolean => {
  let _formComplete = true;
  for (let index = 0; index < mandatoryFields.length; index++) {
    const field = mandatoryFields[index];
    if (!form[field] || form[field].length <= 0) {
      _formComplete = false;
      break;
    }
  }
  return _formComplete;
};

/**
 * Check if an email is valid.
 *
 * @param {string} email - The email to check.
 * @returns {boolean} - Returns true if the email is valid, false otherwise.
 */
export const checkIfEmailValid = (email: string): boolean => {
  const regex = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
  return regex.test(email);
};

/**
 * Check if a password is valid.
 *
 * @param {string} password - The password to check.
 * @returns {boolean} - Returns true if the password is valid, false otherwise.
 */
export const checkIfPasswordValid = (password: string): boolean => {
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{7,}$/;
  return regex.test(password);
};

/**
 * Converts an object to FormData, handling nested objects, arrays, and files.
 *
 * @param {Object} obj - The object to convert to FormData.
 * @param {FormData} [formData] - An existing FormData object (used for recursion).
 * @param {string} [parentKey] - The parent key for nested objects.
 * @returns {FormData} The prepared FormData object.
 */
export const objectToFormData = (
  obj: Record<string, any>,
  formData: FormData = new FormData(),
  parentKey: string = '',
): FormData => {
  Object.entries(obj).forEach(([key, value]) => {
    // Format key for nested objects
    const formKey = parentKey ? `${parentKey}[${key}]` : key;

    if (
      value === null ||
      value === undefined ||
      (key === 'image' && !value?.uri)
    ) {
      return; // Skip null or undefined values
    }

    if (Array.isArray(value)) {
      // Handle arrays by appending each item
      value.forEach((item, index) => {
        formData.append(`${formKey}[${index}]`, String(item));
      });
    } else {
      // Append primitive values as strings
      formData.append(formKey, value);
    }
  });

  return formData;
};
